import { useChartColors } from './chartPalette';

interface TooltipRow {
  /** Series/category name. Rendered with `textContent` semantics (React text
   * nodes, never dangerouslySetInnerHTML) since this can carry API-sourced
   * names (insurer, status, source). */
  label: string;
  value: string;
  color?: string;
}

interface ChartTooltipProps {
  active?: boolean;
  title?: string;
  rows?: TooltipRow[];
}

/**
 * Shared recharts tooltip content. Per the dataviz skill: values lead, labels
 * follow (the number is the strong element, the series name is secondary --
 * the legend's hierarchy inverted, since here the reader already has the
 * series and wants the number); each row keys its series with a short stroke
 * of the series color rather than a filled swatch box.
 */
export function ChartTooltip({ active, title, rows }: ChartTooltipProps) {
  const colors = useChartColors();
  if (!active || !rows || rows.length === 0) return null;

  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.grid}`,
        borderRadius: 6,
        padding: '8px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        fontSize: 12,
      }}
    >
      {title && (
        <div style={{ color: colors.ink.secondary, marginBottom: 4 }}>{title}</div>
      )}
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: i === 0 ? 0 : 2 }}>
          {row.color && (
            <span style={{ display: 'inline-block', width: 10, height: 2, background: row.color, flexShrink: 0 }} />
          )}
          <span style={{ color: colors.ink.primary, fontWeight: 600 }}>{row.value}</span>
          <span style={{ color: colors.ink.secondary }}>{row.label}</span>
        </div>
      ))}
    </div>
  );
}
